/**
 * ContractEditor — Tiptap editor for text templates.
 *
 * Paste text in (Word, email, anywhere); select a word or phrase such as
 * LOCATION and choose a field from the side panel and the selection becomes a
 * merge-field chip. With nothing selected the chip is inserted at the cursor.
 * Images can be added from the toolbar, or pasted/dropped in; they are scaled
 * down and embedded in the text itself (see utils/imageEmbed).
 * onChange receives the HTML, with chips as <span data-merge-field="id">.
 */
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import Underline from '@tiptap/extension-underline';
import { Table } from '@tiptap/extension-table';
import { TableRow } from '@tiptap/extension-table-row';
import { TableMap, cellAround } from '@tiptap/pm/tables';
import { Decoration, DecorationSet } from '@tiptap/pm/view';
import { TableCell } from '@tiptap/extension-table-cell';
import { TableHeader } from '@tiptap/extension-table-header';
import { TextAlign } from '@tiptap/extension-text-align';
import { Plugin, PluginKey, TextSelection } from '@tiptap/pm/state';
import { toast } from 'sonner';
import {
  Bold, Italic, Underline as UnderlineIcon, List, ListOrdered, Link as LinkIcon,
  Table as TableIcon, Heading1, Heading2, Heading3, AlignLeft, AlignCenter,
  AlignRight, Undo2, Redo2, Image as ImageIcon, PenTool,
} from 'lucide-react';
import { Separator } from '../ui/separator';
import MergeField from './MergeFieldNode';
import HeldSelection from './HeldSelection';
import FieldCueColors from './FieldCueColors';
import '../../styles/contractDoc.css';
import SignatureBlock from './SignatureBlock';
import {
  ACCEPTED_IMAGE_TYPES,
  IMAGE_PERCENT_STEP,
  MAX_IMAGE_PERCENT,
  MIN_IMAGE_PERCENT,
  embedImageFile,
  isAcceptedImage,
  widthPercent,
} from '../../utils/imageEmbed';

// Only images embedded as data are ever read back in: a linked or local-file image
// (the usual result of pasting from Word) can't be shown to the candidate and would
// be removed on save anyway.
const ContractImage = Image.extend({
  parseHTML() {
    return [{ tag: 'img[src^="data:image/"]' }];
  },
  addAttributes() {
    return {
      ...this.parent?.(),
      width: {
        default: null,
        parseHTML: (el) => el.getAttribute('width'),
        renderHTML: (attrs) => (attrs.width ? { width: attrs.width } : {}),
      },
    };
  },
}).configure({ inline: true, allowBase64: true });

// A table whose borders can be switched off. Saved as data-borders="none" on the <table>;
// borders are on when there is no attribute.
const BorderedTable = Table.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      borders: {
        default: null,
        parseHTML: (el) => (el.getAttribute('data-borders') === 'none' ? 'none' : null),
        renderHTML: (attrs) => (attrs.borders === 'none' ? { 'data-borders': 'none' } : {}),
      },
    };
  },
  // While editing, the resizable table is drawn by the column-resizing plugin's own view (a
  // .tableWrapper around the <table>), which ignores the attribute above. Mark that wrapper too,
  // so the editor's CSS can see which tables have their borders off.
  addProseMirrorPlugins() {
    return [
      ...(this.parent?.() ?? []),
      new Plugin({
        key: new PluginKey('tableBordersOff'),
        props: {
          decorations: (state) => {
            const decorations = [];
            state.doc.descendants((node, pos) => {
              if (node.type.name !== 'table') return true;
              if (node.attrs.borders === 'none') {
                decorations.push(Decoration.node(pos, pos + node.nodeSize, { 'data-borders': 'none' }));
              }
              return false;
            });
            return DecorationSet.create(state.doc, decorations);
          },
        },
      }),
    ];
  },
});

// A table row that can be given a height (in px). Stored as an inline height on the <tr>,
// which the server keeps (and the signed PDF honours as a minimum row height).
const SizedRow = TableRow.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      height: {
        default: null,
        parseHTML: (el) => {
          const h = parseInt(el.style?.height, 10);
          return Number.isFinite(h) ? h : null;
        },
        renderHTML: (attrs) => (attrs.height ? { style: `height: ${attrs.height}px` } : {}),
      },
    };
  },
});

export const MIN_COLUMN_PX = 40;
export const MAX_COLUMN_PX = 800;
export const MIN_ROW_PX = 20;
export const MAX_ROW_PX = 600;

// A small number box that applies its value when you leave it or press Enter (not on
// every keystroke). Empty means "automatic".
function SizeField({ label, value, min, max, onCommit, testid, title }) {
  const [text, setText] = useState(value ?? '');
  useEffect(() => { setText(value ?? ''); }, [value]);
  const commit = () => {
    const trimmed = String(text).trim();
    if (trimmed === '') { if (value != null) onCommit(null); return; }
    const n = Math.round(Number(trimmed));
    if (!Number.isFinite(n)) { setText(value ?? ''); return; }
    const clamped = Math.min(max, Math.max(min, n));
    setText(clamped);
    if (clamped !== value) onCommit(clamped);
  };
  return (
    <label className="flex items-center gap-1 text-[11px] text-muted-foreground" title={title}>
      {label}
      <input
        type="number"
        min={min}
        max={max}
        step={10}
        value={text}
        placeholder="auto"
        onChange={(e) => setText(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); commit(); } }}
        className="w-16 border rounded px-1 py-0.5 text-[11px] text-foreground bg-white"
        data-testid={testid}
      />
      px
    </label>
  );
}

// What can be done to the table the cursor is in. `command` is the Tiptap table command.
const TABLE_ACTIONS = [
  { id: 'row-above', label: '+ Row above', title: 'Add a row above the cursor', command: 'addRowBefore' },
  { id: 'row-below', label: '+ Row below', title: 'Add a row below the cursor', command: 'addRowAfter' },
  { id: 'col-left', label: '+ Column left', title: 'Add a column to the left of the cursor', command: 'addColumnBefore' },
  { id: 'col-right', label: '+ Column right', title: 'Add a column to the right of the cursor', command: 'addColumnAfter' },
  { id: 'delete-row', label: '− Row', title: 'Delete the row the cursor is in', command: 'deleteRow', danger: true },
  { id: 'delete-col', label: '− Column', title: 'Delete the column the cursor is in', command: 'deleteColumn', danger: true },
  { id: 'header', label: 'Header row', title: 'Turn the first row into a header row (or back)', command: 'toggleHeaderRow' },
  { id: 'delete-table', label: 'Delete table', title: 'Remove the whole table', command: 'deleteTable', danger: true },
];

const CHIP_STYLE = `
/* The editing area's focus ring: the browser's default is drawn flush with (partly inside)
   the edge, which hides the blinking cursor on an empty line at the left margin. Push it 2px
   outside, and keep the text a couple of pixels in from the edge. */
.contract-editor .ProseMirror { padding: 2px 4px; caret-color: #0f172a; }
/* Selected text stays highlighted while focus is elsewhere (see HeldSelection) */
.contract-editor .held-selection { background: #bfdbfe; border-radius: 2px; }
.contract-editor .ProseMirror:focus,
.contract-editor .ProseMirror:focus-visible { outline: 2px solid rgba(2, 132, 199, 0.55); outline-offset: 2px; }
.contract-editor .tableWrapper { overflow-x: auto; }
.contract-editor td, .contract-editor th { position: relative; }
/* Borders switched off: the editor draws dashed lines as a cue. The candidate and the PDF show no lines at all. */
.contract-editor .ProseMirror [data-borders="none"] td, .contract-editor .ProseMirror [data-borders="none"] th { border: 1px dashed #64748b; }
.contract-editor .column-resize-handle { position: absolute; right: -2px; top: 0; bottom: -2px; width: 4px; background: #0284c7; pointer-events: none; }
.contract-editor.resize-cursor, .contract-editor .resize-cursor { cursor: col-resize; }
.contract-editor [data-merge-field] {
  display: inline-block; padding: 0 6px; margin: 0 1px; border-radius: 4px;
  background: #e0f2fe; border: 1px solid #7dd3fc; color: #075985;
  font-size: 0.92em; line-height: 1.5; white-space: nowrap;
}
.contract-editor [data-merge-field].ProseMirror-selectednode { outline: 2px solid #0284c7; }
/* Chip colours by field type. Text is the default blue above. */
.contract-editor [data-merge-field].chip-date { background: #dcfce7; border-color: #86efac; color: #166534; }
.contract-editor [data-merge-field].chip-number { background: #ffedd5; border-color: #fdba74; color: #9a3412; }
.contract-editor [data-merge-field].chip-auto { background: #f3e8ff; border-color: #d8b4fe; color: #6b21a8; }
.contract-editor [data-merge-field].chip-bullhorn { background: #f5efe0; border-color: #d9c8a0; color: #5c4a22; }
.contract-editor img { max-width: 100%; height: auto; }
/* The signature box: shows where the candidate (or, on a contact template, the contact) signs. It can be dragged, or moved with the toolbar button. */
.contract-editor [data-signature-block] { margin: 16px 0; padding: 14px 16px; border: 2px dashed #94a3b8; border-radius: 6px; background: #f8fafc; color: #475569; font-size: 0.9em; cursor: grab; user-select: none; }
.contract-editor [data-signature-block]::before { content: 'Signature — the candidate signs here'; display: block; font-weight: 600; }
.contract-editor[data-signer="contact"] [data-signature-block]::before { content: 'Signature — the contact signs here'; }
.contract-editor [data-signature-block]::after { content: 'Their printed name and the date appear underneath. Drag this box, or use the pen button, to move it.'; display: block; margin-top: 4px; font-size: 0.85em; color: #64748b; }
.contract-editor [data-signature-block].ProseMirror-selectednode { outline: 2px solid #0284c7; }
.contract-editor img.ProseMirror-selectednode { outline: 2px solid #0284c7; }
`;

const ToolbarButton = ({ onClick, active, disabled, title, children, testid }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    title={title}
    data-testid={testid}
    className={`p-1.5 rounded text-sm transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
      active ? 'bg-slate-800 text-white' : 'hover:bg-slate-100 text-slate-700'
    }`}
  >
    {children}
  </button>
);

export const ContractEditor = forwardRef(function ContractEditor(
  { html, onChange, onSelectionChange, fieldCues, signer = 'candidate' },
  ref,
) {
  const fileInputRef = useRef(null);
  // field id -> cue ('text' | 'date' | 'number' | 'auto'), read by FieldCueColors when it draws chips
  const cuesRef = useRef(fieldCues || {});
  const editorRef = useRef(null);

  // Scale each picked/pasted/dropped image down, then put it in the text.
  const insertImages = async (files) => {
    const images = [...files].filter(isAcceptedImage);
    if (!images.length) {
      toast.error('Use a PNG, JPEG, GIF or WebP image.');
      return;
    }
    for (const file of images) {
      try {
        const { src, alt } = await embedImageFile(file);
        editorRef.current?.chain().focus().setImage({ src, alt }).run();
      } catch (error) {
        toast.error(error.message || 'Could not add that image.');
      }
    }
  };

  const editor = useEditor({
    // Tiptap 3 doesn't re-render on every transaction by default; the toolbar's
    // active states and the image controls below depend on it.
    shouldRerenderOnTransaction: true,
    extensions: [
      StarterKit.configure({ heading: { levels: [1, 2, 3] } }),
      Underline,
      Link.configure({ openOnClick: false, autolink: false }),
      // resizable: drag a column border to change that column's width
      BorderedTable.configure({ resizable: true, cellMinWidth: MIN_COLUMN_PX }),
      SizedRow,
      TableHeader,
      TableCell,
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      ContractImage,
      MergeField,
      SignatureBlock,
      HeldSelection,
      FieldCueColors.configure({ getCues: () => cuesRef.current }),
    ],
    content: html || '<p></p>',
    editorProps: {
      // Pasted or dropped image files become embedded images.
      handlePaste: (_view, event) => {
        const files = [...(event.clipboardData?.files || [])].filter((f) => f.type.startsWith('image/'));
        if (!files.length) return false;
        event.preventDefault();
        insertImages(files);
        return true;
      },
      handleDrop: (_view, event) => {
        const files = [...(event.dataTransfer?.files || [])].filter((f) => f.type.startsWith('image/'));
        if (!files.length) return false;
        event.preventDefault();
        insertImages(files);
        return true;
      },
    },
    onUpdate: ({ editor: ed }) => onChange?.(ed.getHTML()),
    onSelectionUpdate: ({ editor: ed }) => {
      const { from, to, empty } = ed.state.selection;
      onSelectionChange?.(empty ? '' : ed.state.doc.textBetween(from, to, ' '));
    },
  });
  editorRef.current = editor;

  // Only reset when the persisted HTML changes from outside (a template load).
  useEffect(() => {
    if (!editor) return;
    if (html != null && html !== editor.getHTML()) editor.commands.setContent(html || '<p></p>', false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [html, editor]);

  // Recolour the chips whenever the template's fields change.
  useEffect(() => {
    cuesRef.current = fieldCues || {};
    if (editor) editor.view.dispatch(editor.state.tr.setMeta('fieldCuesRefresh', true));
  }, [fieldCues, editor]);

  useImperativeHandle(ref, () => ({
    // Replace the current selection (or insert at the cursor) with a chip.
    insertField: ({ id, label, cue }) => {
      if (!editor) return;
      // The field list updates a moment later than this call, so colour the new chip now.
      if (cue) cuesRef.current = { ...cuesRef.current, [id]: cue };
      editor
        .chain()
        .focus()
        .insertContent({ type: 'mergeField', attrs: { fieldId: id, label } })
        .run();
    },
  }), [editor]);

  if (!editor) return null;

  const insertLink = () => {
    const url = window.prompt('URL', 'https://');
    if (url) editor.chain().focus().toggleLink({ href: url }).run();
  };

  const inTable = editor.isActive('table');

  // The table the cursor is in: where it starts, and how many rows and columns it has.
  const currentTable = () => {
    const { $from } = editor.state.selection;
    for (let d = $from.depth; d > 0; d -= 1) {
      const node = $from.node(d);
      if (node.type.name === 'table') {
        return { pos: $from.before(d), rows: node.childCount, cols: node.firstChild?.childCount ?? 0 };
      }
    }
    return null;
  };
  // A table always keeps at least one row and one column (use Delete table to remove it),
  // so those two buttons are off at that point instead of silently doing nothing.
  const canTable = (a) => {
    const table = currentTable();
    if (a.command === 'deleteRow' && table && table.rows <= 1) return false;
    if (a.command === 'deleteColumn' && table && table.cols <= 1) return false;
    return editor.can()[a.command]();
  };
  // The column the cursor is in: its table, its index, and its stored width (px, if set).
  const currentColumn = () => {
    const $cell = cellAround(editor.state.selection.$from);
    if (!$cell) return null;
    const table = $cell.node(-1);
    const start = $cell.start(-1);
    const map = TableMap.get(table);
    const col = map.colCount($cell.pos - start);
    const attrs = $cell.nodeAfter?.attrs ?? {};
    const within = attrs.colspan > 1 ? col - map.colCount($cell.pos - start) : 0;
    return { table, start, map, col, width: attrs.colwidth?.[within] || null };
  };
  // Give the cursor's whole column one width, as dragging its border does (null = automatic).
  const setColumnWidth = (px) => {
    const column = currentColumn();
    if (!column) return;
    const { table, start, map, col } = column;
    const tr = editor.state.tr;
    for (let row = 0; row < map.height; row += 1) {
      const index = row * map.width + col;
      if (row && map.map[index] === map.map[index - map.width]) continue; // spans from the row above
      const pos = map.map[index];
      const attrs = table.nodeAt(pos).attrs;
      const within = attrs.colspan === 1 ? 0 : col - map.colCount(pos);
      const colwidth = attrs.colwidth ? attrs.colwidth.slice() : Array(attrs.colspan).fill(0);
      colwidth[within] = px ?? 0;
      tr.setNodeMarkup(start + pos, null, { ...attrs, colwidth: colwidth.some(Boolean) ? colwidth : null });
    }
    if (tr.docChanged) editor.view.dispatch(tr);
  };
  const bordersOff = inTable && editor.getAttributes('table').borders === 'none';
  const toggleBorders = () => editor.chain().updateAttributes('table', { borders: bordersOff ? null : 'none' }).run();
  const setRowHeight = (px) => editor.chain().updateAttributes('tableRow', { height: px }).run();
  const columnWidth = inTable ? currentColumn()?.width ?? null : null;
  const rowHeight = inTable ? editor.getAttributes('tableRow').height ?? null : null;

  const runTable = (a) => {
    // Remember where the table is: removing the row or column the cursor sits in can
    // leave the cursor outside it, which would hide these controls.
    const tablePos = currentTable()?.pos ?? null;
    editor.chain().focus()[a.command]().run();
    if (tablePos != null && a.command !== 'deleteTable' && !editor.isActive('table')) {
      const { doc } = editor.state;
      if (doc.nodeAt(tablePos)?.type.name === 'table') {
        const inside = TextSelection.near(doc.resolve(tablePos + 1), 1);
        editor.view.dispatch(editor.state.tr.setSelection(inside));
      }
    }
  };

  // Put the signature box after the paragraph (or table) the cursor is in, taking it away
  // from wherever it was. There is always exactly one.
  const moveSignatureHere = () => {
    editor
      .chain()
      .focus()
      .command(({ tr, state, dispatch }) => {
        const type = state.schema.nodes.signatureBlock;
        const existing = [];
        state.doc.descendants((node, pos) => {
          if (node.type === type) { existing.push(pos); return false; }
          return true;
        });
        const { $from } = state.selection;
        const at = $from.depth > 0 ? $from.after(1) : $from.pos;
        tr.insert(at, type.create());
        for (const pos of [...existing].reverse()) {
          const mapped = tr.mapping.map(pos);
          tr.delete(mapped, mapped + 1);
        }
        if (dispatch) dispatch(tr);
        return true;
      })
      .run();
  };

  const imageSelected = editor.isActive('image');
  const imagePercent = imageSelected ? widthPercent(editor.getAttributes('image').width) : null;
  // Changing the width replaces the image node and would drop the selection (and with
  // it these controls), so put the selection back on the same image. No focus() here:
  // it would pull focus off the slider while it is being dragged or moved with the keys.
  const setImageWidth = (width) => {
    const at = editor.state.selection.from;
    editor.chain().updateAttributes('image', { width }).setNodeSelection(at).run();
  };

  return (
    <div className="border rounded-lg bg-white flex flex-col contract-editor h-full min-h-0" data-testid="contract-editor" data-signer={signer}>
      <style>{CHIP_STYLE}</style>
      <div className="flex items-center gap-0.5 px-2 py-1.5 border-b bg-slate-50 flex-wrap">
        <ToolbarButton testid="ce-undo" onClick={() => editor.chain().focus().undo().run()} disabled={!editor.can().undo()} title="Undo"><Undo2 className="w-4 h-4" /></ToolbarButton>
        <ToolbarButton testid="ce-redo" onClick={() => editor.chain().focus().redo().run()} disabled={!editor.can().redo()} title="Redo"><Redo2 className="w-4 h-4" /></ToolbarButton>
        <Separator orientation="vertical" className="h-5 mx-1" />
        <ToolbarButton testid="ce-h1" onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()} active={editor.isActive('heading', { level: 1 })} title="Heading 1"><Heading1 className="w-4 h-4" /></ToolbarButton>
        <ToolbarButton testid="ce-h2" onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} active={editor.isActive('heading', { level: 2 })} title="Heading 2"><Heading2 className="w-4 h-4" /></ToolbarButton>
        <ToolbarButton testid="ce-h3" onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} active={editor.isActive('heading', { level: 3 })} title="Heading 3"><Heading3 className="w-4 h-4" /></ToolbarButton>
        <Separator orientation="vertical" className="h-5 mx-1" />
        <ToolbarButton testid="ce-bold" onClick={() => editor.chain().focus().toggleBold().run()} active={editor.isActive('bold')} title="Bold"><Bold className="w-4 h-4" /></ToolbarButton>
        <ToolbarButton testid="ce-italic" onClick={() => editor.chain().focus().toggleItalic().run()} active={editor.isActive('italic')} title="Italic"><Italic className="w-4 h-4" /></ToolbarButton>
        <ToolbarButton testid="ce-underline" onClick={() => editor.chain().focus().toggleUnderline().run()} active={editor.isActive('underline')} title="Underline"><UnderlineIcon className="w-4 h-4" /></ToolbarButton>
        <Separator orientation="vertical" className="h-5 mx-1" />
        <ToolbarButton testid="ce-bullet" onClick={() => editor.chain().focus().toggleBulletList().run()} active={editor.isActive('bulletList')} title="Bullet list"><List className="w-4 h-4" /></ToolbarButton>
        <ToolbarButton testid="ce-ordered" onClick={() => editor.chain().focus().toggleOrderedList().run()} active={editor.isActive('orderedList')} title="Numbered list"><ListOrdered className="w-4 h-4" /></ToolbarButton>
        <Separator orientation="vertical" className="h-5 mx-1" />
        <ToolbarButton testid="ce-left" onClick={() => editor.chain().focus().setTextAlign('left').run()} active={editor.isActive({ textAlign: 'left' })} title="Align left"><AlignLeft className="w-4 h-4" /></ToolbarButton>
        <ToolbarButton testid="ce-center" onClick={() => editor.chain().focus().setTextAlign('center').run()} active={editor.isActive({ textAlign: 'center' })} title="Align centre"><AlignCenter className="w-4 h-4" /></ToolbarButton>
        <ToolbarButton testid="ce-right" onClick={() => editor.chain().focus().setTextAlign('right').run()} active={editor.isActive({ textAlign: 'right' })} title="Align right"><AlignRight className="w-4 h-4" /></ToolbarButton>
        <Separator orientation="vertical" className="h-5 mx-1" />
        <ToolbarButton testid="ce-link" onClick={insertLink} title="Insert link"><LinkIcon className="w-4 h-4" /></ToolbarButton>
        <ToolbarButton testid="ce-table" onClick={() => editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()} title="Insert table"><TableIcon className="w-4 h-4" /></ToolbarButton>
        <ToolbarButton testid="ce-image" onClick={() => fileInputRef.current?.click()} title="Insert image"><ImageIcon className="w-4 h-4" /></ToolbarButton>
        <ToolbarButton testid="ce-signature" onClick={moveSignatureHere} title="Move the signature box to the cursor"><PenTool className="w-4 h-4" /></ToolbarButton>
        <input
          ref={fileInputRef}
          type="file"
          accept={ACCEPTED_IMAGE_TYPES.join(',')}
          multiple
          className="hidden"
          data-testid="ce-image-input"
          onChange={(e) => {
            insertImages(e.target.files);
            e.target.value = ''; // allow choosing the same file again
          }}
        />

        {/* Size of the selected image, as a share of the page width */}
        {imageSelected && (
          <div className="flex items-center gap-1 ml-2 pl-2 border-l" data-testid="ce-image-sizes">
            <label htmlFor="ce-image-width" className="text-[11px] text-muted-foreground mr-1">Image width</label>
            <input
              id="ce-image-width"
              type="range"
              min={MIN_IMAGE_PERCENT}
              max={MAX_IMAGE_PERCENT}
              step={IMAGE_PERCENT_STEP}
              value={imagePercent ?? MAX_IMAGE_PERCENT}
              onChange={(e) => setImageWidth(`${e.target.value}%`)}
              className="w-32 accent-slate-800"
              aria-valuetext={imagePercent ? `${imagePercent}% of the page width` : 'Original size'}
              data-testid="ce-image-width-slider"
            />
            <span className="text-[11px] w-12 tabular-nums" data-testid="ce-image-width-value">
              {imagePercent ? `${imagePercent}%` : 'Original'}
            </span>
            {imagePercent && (
              <button
                type="button"
                onClick={() => setImageWidth(null)}
                data-testid="ce-image-width-reset"
                className="px-1.5 py-0.5 rounded text-[11px] border hover:bg-slate-100"
                title="Show the image at its natural size"
              >
                Reset
              </button>
            )}
          </div>
        )}

        {/* Edit the table the cursor is in: add or remove rows and columns */}
        {inTable && (
          <div className="flex items-center gap-1 ml-2 pl-2 border-l flex-wrap" data-testid="ce-table-controls">
            <span className="text-[11px] text-muted-foreground mr-1">Table</span>
            {/* Size of the column and row the cursor is in. You can also drag a column border. */}
            <SizeField
              label="Column width"
              value={columnWidth}
              min={MIN_COLUMN_PX}
              max={MAX_COLUMN_PX}
              onCommit={setColumnWidth}
              title="Width of the column the cursor is in. You can also drag a column's border."
              testid="ce-table-column-width"
            />
            <SizeField
              label="Row height"
              value={rowHeight}
              min={MIN_ROW_PX}
              max={MAX_ROW_PX}
              onCommit={setRowHeight}
              title="Height of the row the cursor is in (the row grows if its text needs more room)."
              testid="ce-table-row-height"
            />
            <button
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={toggleBorders}
              title="Show or hide the lines around the cells (when off, the editor shows dashed lines as a cue; they are not shown in the final document)"
              aria-pressed={!bordersOff}
              data-testid="ce-table-borders"
              className={`px-1.5 py-0.5 rounded text-[11px] border ${bordersOff ? 'hover:bg-slate-100' : 'bg-sky-100 border-sky-300 text-sky-800'}`}
            >
              Borders: {bordersOff ? 'Off' : 'On'}
            </button>
            {TABLE_ACTIONS.map((a) => (
              <button
                key={a.id}
                type="button"
                // Keep the cursor in its cell: the commands act on where it is.
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => runTable(a)}
                disabled={!canTable(a)}
                title={a.title}
                data-testid={`ce-table-${a.id}`}
                className={`px-1.5 py-0.5 rounded text-[11px] border disabled:opacity-40 disabled:cursor-not-allowed ${
                  a.danger ? 'text-red-700 border-red-200 hover:bg-red-50' : 'hover:bg-slate-100'
                }`}
              >
                {a.label}
              </button>
            ))}
          </div>
        )}
      </div>
      <div className="flex-1 min-h-0 overflow-y-auto bg-slate-100 p-6">
        <div className="max-w-[850px] mx-auto bg-white shadow-sm rounded">
          <EditorContent
            editor={editor}
            data-testid="ce-content"
            className="contract-doc p-10 min-h-[900px] focus:outline-none"
          />
        </div>
      </div>
    </div>
  );
});

export default ContractEditor;
